import { Component, Input, Output, EventEmitter, Optional, Self } from '@angular/core';
import { ControlValueAccessor, NgControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'lib-checkbox',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './checkbox.component.html',
  styleUrl: './checkbox.component.scss',
})
export class CheckboxComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() disabled = false;
  @Input() required = false;
  @Input() errorMessage = '';
  @Input() hint = '';
  @Input() indeterminate = false;
  /** When set, checkbox works in controlled mode: parent binds [checked] and (checkedChange). */
  @Input() checked = false;

  @Output() checkedChange = new EventEmitter<boolean>();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange = (_value: boolean) => {
    // ControlValueAccessor interface requirement
  };
  onTouched = () => {
    // ControlValueAccessor interface requirement
  };

  // ControlValueAccessor pattern requires NgControl injection via constructor
  // This is the standard Angular pattern for custom form controls
  // eslint-disable-next-line
  constructor(@Optional() @Self() public ngControl: NgControl | null) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  get hasError(): boolean {
    if (this.errorMessage) {
      return true;
    }
    if (this.ngControl?.errors && this.ngControl?.touched) {
      return true;
    }
    return false;
  }

  get errorText(): string {
    if (this.errorMessage) {
      return this.errorMessage;
    }
    if (this.ngControl?.errors && this.ngControl?.touched) {
      const errors = this.ngControl.errors;
      if (errors['required']) {
        return 'This field is required';
      }
      return 'Invalid value';
    }
    return '';
  }

  onCheckboxChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.checked = target.checked;
    this.onChange(this.checked);
    this.checkedChange.emit(this.checked);
    this.onTouched();
  }

  // ControlValueAccessor implementation
  writeValue(value: boolean): void {
    this.checked = !!value;
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}

